import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { ShieldCheck, Send, Eye, Loader2, AlertCircle, CheckCircle2, Lock, LogOut, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'atf_admin_token';

const DEFAULT_SUBJECT = 'Aloha Tequila Fest · Presale tickets are LIVE — prices go up June 7 🌺🍹';
const DEFAULT_HTML = `<!doctype html>
<html><body style="margin:0;padding:0;background:#0a0a0a;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#fff;">
  <div style="max-width:560px;margin:0 auto;padding:32px 24px;background:linear-gradient(180deg,#1a0a14 0%,#0a0a0a 100%);">
    <h1 style="font-size:28px;font-weight:900;background:linear-gradient(90deg,#ec4899,#f97316,#fbbf24);-webkit-background-clip:text;-webkit-text-fill-color:transparent;margin:0 0 16px;">
      Presale tickets are LIVE 🌺
    </h1>
    <p style="font-size:16px;line-height:1.6;color:#e5e5e5;margin:0 0 16px;">
      Aloha — Presale pricing for the 1st Annual <strong>Aloha Tequila &amp; Arts Festival</strong> is on right now, and tickets are moving fast.
    </p>
    <div style="background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.12);border-radius:16px;padding:20px;margin:20px 0;">
      <div style="font-size:14px;color:#a3a3a3;margin-bottom:8px;">Saturday, June 20, 2026 · 5–9 PM · International Market Place, Waikiki</div>
      <table cellpadding="0" cellspacing="0" style="width:100%;border-collapse:collapse;">
        <tr>
          <td style="padding:8px 0;font-size:15px;color:#fff;">General Admission</td>
          <td style="padding:8px 0;font-size:20px;font-weight:900;text-align:right;color:#fbbf24;">$65</td>
        </tr>
        <tr>
          <td style="padding:8px 0;font-size:15px;color:#fff;border-top:1px solid rgba(255,255,255,0.1);">VIP Experience</td>
          <td style="padding:8px 0;font-size:20px;font-weight:900;text-align:right;color:#fbbf24;border-top:1px solid rgba(255,255,255,0.1);">$110</td>
        </tr>
      </table>
      <p style="font-size:13px;color:#fbbf24;font-weight:700;margin:12px 0 0;">Heads up — prices go up June 7 to $75 GA / $125 VIP. Tickets are selling fast.</p>
    </div>
    <p style="text-align:center;margin:28px 0;">
      <a href="https://alohacancer.ticketspice.com/aloha-tequila-arts-festival"
         style="display:inline-block;background:linear-gradient(90deg,#ec4899,#f97316,#fbbf24);color:#0a0a0a;font-weight:900;font-size:16px;text-decoration:none;padding:14px 32px;border-radius:9999px;">
        Lock in Presale Pricing →
      </a>
    </p>
    <p style="font-size:13px;color:#737373;line-height:1.6;margin:24px 0 0;text-align:center;">
      21+ event · Valid government-issued ID required at entry · Partial proceeds benefit the Aloha Cancer Project.<br/>
      <a href="https://www.alohatequilafest.com" style="color:#a3a3a3;">www.alohatequilafest.com</a>
    </p>
  </div>
</body></html>`;

const AdminBlastPage = () => {
  const [token, setToken] = useState('');
  const [authed, setAuthed] = useState(false);
  const [count, setCount] = useState(null);
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [html, setHtml] = useState(DEFAULT_HTML);
  const [showPreview, setShowPreview] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  // On mount, try saved token
  useEffect(() => {
    const saved = localStorage.getItem(TOKEN_KEY);
    if (saved) {
      setToken(saved);
      // Validate by fetching subscriber count
      validateToken(saved);
    }
  }, []);

  const validateToken = async (t) => {
    setErrorMsg('');
    try {
      const res = await fetch(`${API_URL}/api/admin/subscribers/count`, {
        headers: { 'X-Admin-Token': t }
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setCount(data.active_subscribers);
      setAuthed(true);
      localStorage.setItem(TOKEN_KEY, t);
    } catch (err) {
      setAuthed(false);
      setErrorMsg(typeof err.message === 'string' ? err.message : 'Invalid token');
      localStorage.removeItem(TOKEN_KEY);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    validateToken(token.trim());
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    setToken('');
    setCount(null);
    setResult(null);
  };

  const handleSend = async (dryRun) => {
    if (!dryRun) {
      const confirmed = window.confirm(
        `Send to ${count} active subscriber${count === 1 ? '' : 's'}? This cannot be undone.`
      );
      if (!confirmed) return;
    }
    setStatus('sending');
    setErrorMsg('');
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/blast`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Token': token
        },
        body: JSON.stringify({ subject, html_body: html, dry_run: dryRun })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setResult(data);
      setStatus('done');
    } catch (err) {
      setStatus('error');
      setErrorMsg(typeof err.message === 'string' ? err.message : 'Send failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="admin-blast-page">
      <Helmet>
        <title>Admin · Newsletter Blast</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 py-10 sm:py-16">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-pink-500 to-orange-500 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-black">Newsletter Blast</h1>
            <p className="text-white/55 text-sm">Token-protected · admin only</p>
          </div>
          {authed && (
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="ml-auto inline-flex items-center gap-1.5 text-white/55 hover:text-white text-sm"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          )}
        </div>

        {!authed ? (
          <Card className="bg-white/5 border-white/10 p-6 rounded-2xl">
            <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-pink-400" />
                <span className="text-sm font-bold">Enter admin token</span>
              </div>
              <Input
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Admin token"
                data-testid="admin-token-input"
                className="bg-white/5 border-white/15 text-white placeholder:text-white/35"
                autoFocus
              />
              {errorMsg && (
                <div className="flex items-start gap-2 text-red-300 text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-3" data-testid="admin-login-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{errorMsg}</span>
                </div>
              )}
              <Button
                type="submit"
                data-testid="admin-login-submit"
                className="w-full bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white font-bold"
              >
                Unlock
              </Button>
            </form>
          </Card>
        ) : (
          <>
            {/* Subscriber count */}
            <Card className="bg-gradient-to-br from-pink-500/15 to-orange-500/10 border-pink-500/30 p-5 rounded-2xl mb-6">
              <div className="flex items-center gap-3">
                <Mail className="w-6 h-6 text-pink-400" />
                <div>
                  <div className="text-3xl font-black tabular-nums" data-testid="admin-subscriber-count">
                    {count ?? '—'}
                  </div>
                  <div className="text-white/65 text-sm">active newsletter subscribers</div>
                </div>
              </div>
            </Card>

            {/* Compose */}
            <Card className="bg-white/5 border-white/10 p-5 sm:p-6 rounded-2xl space-y-4 mb-6">
              <div>
                <Label htmlFor="blast-subject" className="text-white/85 text-sm mb-1.5 block">Subject</Label>
                <Input
                  id="blast-subject"
                  data-testid="blast-subject-input"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="bg-white/5 border-white/15 text-white"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label htmlFor="blast-html" className="text-white/85 text-sm">HTML body</Label>
                  <button
                    type="button"
                    onClick={() => setShowPreview((p) => !p)}
                    data-testid="blast-preview-toggle"
                    className="inline-flex items-center gap-1.5 text-pink-300 hover:text-pink-200 text-xs"
                  >
                    <Eye className="w-3.5 h-3.5" /> {showPreview ? 'Edit HTML' : 'Preview'}
                  </button>
                </div>
                {showPreview ? (
                  <div className="rounded-lg border border-white/15 bg-white overflow-hidden" data-testid="blast-preview-frame">
                    <iframe
                      title="Email preview"
                      srcDoc={html}
                      className="w-full h-[480px] bg-white"
                      sandbox=""
                    />
                  </div>
                ) : (
                  <Textarea
                    id="blast-html"
                    data-testid="blast-html-input"
                    rows={14}
                    value={html}
                    onChange={(e) => setHtml(e.target.value)}
                    className="bg-white/5 border-white/15 text-white font-mono text-xs resize-y"
                  />
                )}
              </div>
            </Card>

            {/* Send actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => handleSend(true)}
                disabled={status === 'sending'}
                data-testid="blast-dry-run"
                variant="outline"
                className="flex-1 bg-white/5 border-white/20 text-white hover:bg-white/10"
              >
                Dry run (no send)
              </Button>
              <Button
                onClick={() => handleSend(false)}
                disabled={status === 'sending' || !count}
                data-testid="blast-send"
                className="flex-1 bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white font-bold"
              >
                {status === 'sending' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending…
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" /> Send to {count ?? '—'} subscribers
                  </>
                )}
              </Button>
            </div>

            {/* Result */}
            {result && (
              <Card className="bg-green-500/10 border-green-500/30 p-5 rounded-2xl mt-6" data-testid="blast-result">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-bold text-white">
                      {result.dry_run ? 'Dry run complete' : 'Blast sent'}
                    </div>
                    <div className="text-white/75 text-sm mt-1">
                      {result.dry_run
                        ? `Would send to ${result.total_subscribers} active subscriber${result.total_subscribers === 1 ? '' : 's'}.`
                        : `Sent ${result.sent} of ${result.total_subscribers}${result.failed ? ` · ${result.failed} failed` : ''}.`}
                    </div>
                    {result.failed_emails && result.failed_emails.length > 0 && (
                      <details className="mt-2">
                        <summary className="text-red-300 text-xs cursor-pointer">Show failed addresses</summary>
                        <pre className="text-red-200 text-xs mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-all">
                          {result.failed_emails.join('\n')}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {errorMsg && status === 'error' && (
              <div className="flex items-start gap-2 text-red-300 text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-3 mt-6" data-testid="blast-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{errorMsg}</span>
              </div>
            )}

            <p className="text-white/45 text-xs text-center mt-8">
              Tokens are stored only in this browser. Sign out to clear.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminBlastPage;
